import React, { useState, useMemo } from 'react';
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButton,
  IonIcon,
  IonCard,
  IonCardContent,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonLabel,
  IonItem,
  IonThumbnail,
  IonImg,
  IonFab,
  IonModal,
  IonButtons,
  IonSegment,
  IonSegmentButton,
  IonList,
  IonListHeader,
  IonToggle,
  IonInput,
  IonTextarea,
  IonNote,
  IonLoading,
  IonAlert,
  IonActionSheet,
  IonAvatar,
  IonGrid,
  IonRow,
  IonCol,
} from '@ionic/react';
import {
  addOutline,
  createOutline,
  trashOutline,
  cameraOutline,
  imageOutline,
  arrowDownOutline,
  arrowUpOutline,
  optionsOutline,
  searchOutline,
  filterOutline,
  ellipsisHorizontalOutline,
  businessOutline,
  checkmarkCircleOutline,
  closeCircleOutline,
  warningOutline,
} from 'ionicons/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IProduct, CreateProductDto, ICategory, MenuItemType, Unit } from 'shared';
import apiClient from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import { uploadApi } from '../api/uploads';
import MetricCard from '../components/MetricCard';

interface ProductFormData extends CreateProductDto {
  materialName?: string;
  materialUnit?: Unit;
  materialCostPerUnit?: number;
  materialCurrentStock?: number;
  materialMinStockLevel?: number;
}

export default function Products() {
  const { t } = useLanguage();
  const [showModal, setShowModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<string>('all');
  const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [showImageUpload, setShowImageUpload] = useState(false);

  const queryClient = useQueryClient();

  // Fetch products
  const { data: products, isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const { data } = await apiClient.get<IProduct[]>('/products');
      return data;
    },
  });

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data } = await apiClient.get<ICategory[]>('/categories');
      return Array.isArray(data) ? data.filter((cat) => cat.status === 'active') : [];
    },
  });

  // Create product mutation
  const createMutation = useMutation({
    mutationFn: async (values: ProductFormData) => {
      const {
        materialName,
        materialUnit,
        materialCostPerUnit,
        materialCurrentStock,
        materialMinStockLevel,
        ...productDto
      } = values;

      const { data: product } = await apiClient.post<IProduct>('/products', productDto);

      // Create material for direct products
      if (
        product.type === MenuItemType.DIRECT &&
        product.rawMaterialId &&
        materialUnit
      ) {
        await apiClient.put(`/materials/${product.rawMaterialId}`, {
          name: materialName || product.name,
          unit: materialUnit,
          costPerUnit: Number(materialCostPerUnit ?? 0),
          currentStock: Number(materialCurrentStock ?? 0),
          minStockLevel: Number(materialMinStockLevel ?? 0),
          isIngredient: false,
        });
      }

      return product;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setShowModal(false);
      // Show success message
    },
    onError: (error) => {
      console.error('Failed to create product:', error);
    },
  });

  // Update product mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateProductDto> }) => {
      await apiClient.put(`/products/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      // Show success message
    },
    onError: (error) => {
      console.error('Failed to update product:', error);
    },
  });

  // Delete product mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/products/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      // Show success message
    },
    onError: (error) => {
      console.error('Failed to delete product:', error);
    },
  });

  // Handle image upload
  const handleImageUpload = async (file: File): Promise<string> => {
    try {
      const { url } = await uploadApi.uploadImage(file);
      return url;
    } catch (error) {
      console.error('Failed to upload image:', error);
      throw error;
    }
  };

  // Calculate metrics
  const totalProducts = Array.isArray(products) ? products.length : 0;
  const activeProducts = Array.isArray(products) ? products.filter(p => p.status === 'active').length : 0;
  const inactiveProducts = Array.isArray(products) ? products.filter(p => p.status === 'inactive').length : 0;
  const manufacturedProducts = Array.isArray(products) ? products.filter(p => p.type === MenuItemType.MANUFACTURED).length : 0;
  const directProducts = Array.isArray(products) ? products.filter(p => p.type === MenuItemType.DIRECT).length : 0;

  // Filter products
  const filteredProducts = useMemo(() => {
    if (!Array.isArray(products)) return [];

    return products.filter((product) => {
      const matchesSearch = product.name
        .toLowerCase()
        .includes(searchText.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || product.categoryId === categoryFilter;
      const matchesType = typeFilter === 'all' || product.type === typeFilter;
      const matchesStatus = statusFilter === 'all' || product.status === statusFilter;

      // Tab filtering
      const matchesTab =
        activeTab === 'all' ||
        (activeTab === 'active' && product.status === 'active') ||
        (activeTab === 'inactive' && product.status === 'inactive') ||
        (activeTab === 'manufactured' && product.type === MenuItemType.MANUFACTURED) ||
        (activeTab === 'direct' && product.type === MenuItemType.DIRECT);

      return matchesSearch && matchesCategory && matchesType && matchesStatus && matchesTab;
    });
  }, [products, searchText, categoryFilter, typeFilter, statusFilter, activeTab]);

  const tabs = [
    { key: 'all', label: t('all') || 'All' },
    { key: 'active', label: t('active') || 'Active' },
    { key: 'inactive', label: t('inactive') || 'Inactive' },
    { key: 'manufactured', label: t('manufactured') || 'Manufactured' },
    { key: 'direct', label: t('direct') || 'Direct' },
  ];

  const handleProductAction = (product: IProduct, action: string) => {
    setSelectedProduct(product);

    switch (action) {
      case 'edit':
        setShowModal(true);
        break;
      case 'delete':
        // Show delete confirmation
        break;
      case 'toggleStatus':
        updateMutation.mutate({
          id: product.id,
          data: { status: product.status === 'active' ? 'inactive' : 'active' }
        });
        break;
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t('productsManagement') || 'Products Management'}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => setShowModal(true)}>
              <IonIcon icon={addOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        {/* Metrics Cards */}
        <IonGrid>
          <IonRow>
            <IonCol size="6">
              <MetricCard
                title={t('totalProducts') || 'Total Products'}
                value={totalProducts.toString()}
                icon={businessOutline}
                accent="primary"
                subtitle={`${manufacturedProducts} ${t('manufactured') || 'manufactured'}`}
              />
            </IonCol>
            <IonCol size="6">
              <MetricCard
                title={t('activeProducts') || 'Active Products'}
                value={activeProducts.toString()}
                icon={checkmarkCircleOutline}
                accent="success"
                subtitle={`${Math.round((activeProducts / totalProducts) * 100 || 0)}%`}
              />
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol size="6">
              <MetricCard
                title={t('inactiveProducts') || 'Inactive Products'}
                value={inactiveProducts.toString()}
                icon={closeCircleOutline}
                accent="warning"
                subtitle={`${Math.round((inactiveProducts / totalProducts) * 100 || 0)}%`}
              />
            </IonCol>
            <IonCol size="6">
              <MetricCard
                title={t('categories') || 'Categories'}
                value={(Array.isArray(categories) ? categories.length : 0).toString()}
                icon={warningOutline}
                accent="medium"
                subtitle={t('productCategories') || 'Product categories'}
              />
            </IonCol>
          </IonRow>
        </IonGrid>

        {/* Search and Filters */}
        <IonCard>
          <IonCardContent>
            <IonSearchbar
              value={searchText}
              onIonInput={(e) => setSearchText(e.detail.value!)}
              placeholder={t('searchProducts') || 'Search products...'}
            />

            <IonGrid>
              <IonRow>
                <IonCol size="4">
                  <IonSelect
                    value={categoryFilter}
                    onIonChange={(e) => setCategoryFilter(e.detail.value)}
                    placeholder={t('category') || 'Category'}
                  >
                    <IonSelectOption value="all">{t('allCategories') || 'All Categories'}</IonSelectOption>
                    {Array.isArray(categories) ? categories.map((cat) => (
                      <IonSelectOption key={cat.id} value={cat.id}>
                        {cat.name}
                      </IonSelectOption>
                    )) : null}
                  </IonSelect>
                </IonCol>
                <IonCol size="4">
                  <IonSelect
                    value={typeFilter}
                    onIonChange={(e) => setTypeFilter(e.detail.value)}
                    placeholder={t('type') || 'Type'}
                  >
                    <IonSelectOption value="all">{t('allTypes') || 'All Types'}</IonSelectOption>
                    <IonSelectOption value={MenuItemType.MANUFACTURED}>{t('manufactured') || 'Manufactured'}</IonSelectOption>
                    <IonSelectOption value={MenuItemType.DIRECT}>{t('direct') || 'Direct'}</IonSelectOption>
                  </IonSelect>
                </IonCol>
                <IonCol size="4">
                  <IonSelect
                    value={statusFilter}
                    onIonChange={(e) => setStatusFilter(e.detail.value)}
                    placeholder={t('status') || 'Status'}
                  >
                    <IonSelectOption value="all">{t('allStatuses') || 'All Statuses'}</IonSelectOption>
                    <IonSelectOption value="active">{t('active') || 'Active'}</IonSelectOption>
                    <IonSelectOption value="inactive">{t('inactive') || 'Inactive'}</IonSelectOption>
                  </IonSelect>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonCardContent>
        </IonCard>

        {/* Tab Navigation */}
        <IonSegment
          value={activeTab}
          onIonChange={(e) => setActiveTab(e.detail.value?.toString() || 'all')}
        >
          {tabs.map((tab) => (
            <IonSegmentButton key={tab.key} value={tab.key}>
              <IonLabel>{tab.label}</IonLabel>
            </IonSegmentButton>
          ))}
        </IonSegment>

        {/* Products List */}
        <IonList>
          <IonListHeader>
            {t('productsList') || 'Products List'} ({filteredProducts.length})
          </IonListHeader>

          {isLoading ? (
            <IonLoading isOpen={true} message={t('loading') || 'Loading...'} />
          ) : (
            filteredProducts.map((product) => (
              <IonItem key={product.id}>
                <IonThumbnail slot="start">
                  {product.image ? (
                    <IonImg src={product.image} alt={product.name} />
                  ) : (
                    <div style={{
                      width: 60,
                      height: 60,
                      backgroundColor: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 8,
                    }}>
                      <IonIcon icon={imageOutline} color="#999" />
                    </div>
                  )}
                </IonThumbnail>

                <IonLabel>
                  <h2>{product.name}</h2>
                  <p>{product.type === MenuItemType.MANUFACTURED ? t('manufactured') : t('direct')}</p>
                  <IonNote color={product.status === 'active' ? 'success' : 'medium'}>
                    {product.status === 'active' ? t('active') : t('inactive')}
                  </IonNote>
                </IonLabel>

                <IonButton
                  slot="end"
                  fill="clear"
                  onClick={() => setShowActionSheet(true)}
                >
                  <IonIcon icon={ellipsisHorizontalOutline} />
                </IonButton>
              </IonItem>
            ))
          )}
        </IonList>

        {filteredProducts.length === 0 && !isLoading && (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#666'
          }}>
            <IonIcon icon={businessOutline} style={{ fontSize: '48px', marginBottom: '16px' }} />
            <p>{t('noProductsFound') || 'No products found'}</p>
            <p style={{ fontSize: '14px' }}>
              {t('tryAdjustingFilters') || 'Try adjusting your filters'}
            </p>
          </div>
        )}

        {/* Add Product Modal */}
        <IonModal isOpen={showModal} onDidDismiss={() => setShowModal(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{t('addProduct') || 'Add Product'}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowModal(false)}>
                  {t('close') || 'Close'}
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <ProductForm
              categories={categories || []}
              onSubmit={(values) => createMutation.mutate(values)}
              loading={createMutation.isPending}
            />
          </IonContent>
        </IonModal>

        {/* Action Sheet */}
        <IonActionSheet
          isOpen={showActionSheet}
          onDidDismiss={() => {
            setShowActionSheet(false);
            setSelectedProduct(null);
          }}
          header={selectedProduct?.name}
          buttons={[
            {
              text: t('edit') || 'Edit',
              icon: createOutline,
              handler: () => {
                if (selectedProduct) {
                  handleProductAction(selectedProduct, 'edit');
                }
              },
            },
            {
              text: selectedProduct?.status === 'active'
                ? (t('deactivate') || 'Deactivate')
                : (t('activate') || 'Activate'),
              icon: selectedProduct?.status === 'active' ? closeCircleOutline : checkmarkCircleOutline,
              handler: () => {
                if (selectedProduct) {
                  handleProductAction(selectedProduct, 'toggleStatus');
                }
              },
            },
            {
              text: t('delete') || 'Delete',
              icon: trashOutline,
              role: 'destructive',
              handler: () => {
                if (selectedProduct) {
                  handleProductAction(selectedProduct, 'delete');
                }
              },
            },
            {
              text: t('cancel') || 'Cancel',
              role: 'cancel',
            },
          ]}
        />
      </IonContent>
    </IonPage>
  );
}

// Product Form Component
interface ProductFormProps {
  categories: ICategory[];
  onSubmit: (values: ProductFormData) => void;
  loading: boolean;
}

function ProductForm({ categories, onSubmit, loading }: ProductFormProps) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    categoryId: '',
    type: MenuItemType.MANUFACTURED,
    status: 'active',
    materialName: '',
    materialUnit: undefined,
    materialCostPerUnit: undefined,
    materialCurrentStock: undefined,
    materialMinStockLevel: undefined,
  });

  const unitOptions = [
    { value: Unit.GRAM, label: t('unit.g') || t('grams') },
    { value: Unit.KILOGRAM, label: t('unit.kg') || t('kilograms') },
    { value: Unit.MILLILITER, label: t('unit.ml') || t('milliliters') },
    { value: Unit.LITER, label: t('unit.l') || t('liters') },
    { value: Unit.BOTTLE, label: t('unit.bottle') || 'Bottle' },
    { value: Unit.CAN, label: t('unit.can') || 'Can' },
    { value: Unit.PACKED, label: t('pieces') || 'Pieces' },
  ];

  const handleSubmit = () => {
    onSubmit(formData);
  };

  return (
    <div style={{ padding: '16px' }}>
      {/* Product Details */}
      <h3 style={{ marginBottom: '16px' }}>{t('productDetails') || 'Product Details'}</h3>

      <IonItem>
        <IonLabel position="stacked">{t('productName') || 'Product Name'}</IonLabel>
        <IonInput
          value={formData.name}
          onIonInput={(e) => setFormData({ ...formData, name: e.detail.value! })}
          placeholder="e.g., Cafe đen đá, Coca-Cola, etc."
        />
      </IonItem>

      <IonItem>
        <IonLabel position="stacked">{t('category') || 'Category'}</IonLabel>
        <IonSelect
          value={formData.categoryId}
          onIonChange={(e) => setFormData({ ...formData, categoryId: e.detail.value })}
          placeholder={t('selectCategory') || 'Select Category'}
        >
          {categories.map((cat) => (
            <IonSelectOption key={cat.id} value={cat.id}>
              {cat.name}
            </IonSelectOption>
          ))}
        </IonSelect>
      </IonItem>

      <IonItem>
        <IonLabel position="stacked">{t('productType') || 'Product Type'}</IonLabel>
        <IonSelect
          value={formData.type}
          onIonChange={(e) => {
            const newType = e.detail.value as MenuItemType;
            setFormData({
              ...formData,
              type: newType,
              // Reset material fields when switching away from direct
              ...(newType !== MenuItemType.DIRECT && {
                materialName: '',
                materialUnit: undefined,
                materialCostPerUnit: undefined,
                materialCurrentStock: undefined,
                materialMinStockLevel: undefined,
              })
            });
          }}
        >
          <IonSelectOption value={MenuItemType.MANUFACTURED}>{t('manufactured') || 'Manufactured'}</IonSelectOption>
          <IonSelectOption value={MenuItemType.DIRECT}>{t('direct') || 'Direct'}</IonSelectOption>
        </IonSelect>
      </IonItem>

      <IonItem>
        <IonLabel position="stacked">{t('status') || 'Status'}</IonLabel>
        <IonToggle
          checked={formData.status === 'active'}
          onIonChange={(e) => setFormData({ ...formData, status: e.detail.checked ? 'active' : 'inactive' })}
        />
      </IonItem>

      {/* Material Configuration for Direct Products */}
      {formData.type === MenuItemType.DIRECT && (
        <>
          <div style={{ margin: '20px 0' }}>
            <h3>{t('materialConfiguration') || 'Material Configuration'}</h3>
            <p style={{ color: '#666', fontSize: '14px' }}>
              {t('materialConfigurationDescription') || 'Configure the raw material for this direct product'}
            </p>
          </div>

          <IonItem>
            <IonLabel position="stacked">{t('materialName') || 'Material Name'}</IonLabel>
            <IonInput
              value={formData.materialName}
              onIonInput={(e) => setFormData({ ...formData, materialName: e.detail.value! })}
              placeholder={t('materialName') || 'Material Name'}
            />
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">{t('unit') || 'Unit'}</IonLabel>
            <IonSelect
              value={formData.materialUnit}
              onIonChange={(e) => setFormData({ ...formData, materialUnit: e.detail.value as Unit })}
              placeholder={t('selectUnit') || 'Select Unit'}
            >
              {unitOptions.map((option) => (
                <IonSelectOption key={option.value} value={option.value}>
                  {option.label}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">{t('costPerUnit') || 'Cost Per Unit'}</IonLabel>
            <IonInput
              type="number"
              value={formData.materialCostPerUnit?.toString() || ''}
              onIonInput={(e) => setFormData({ ...formData, materialCostPerUnit: parseFloat(e.detail.value!) || 0 })}
              placeholder="0"
            />
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">{t('currentStock') || 'Current Stock'}</IonLabel>
            <IonInput
              type="number"
              value={formData.materialCurrentStock?.toString() || ''}
              onIonInput={(e) => setFormData({ ...formData, materialCurrentStock: parseFloat(e.detail.value!) || 0 })}
              placeholder="0"
            />
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">{t('minStockLevel') || 'Min Stock Level'}</IonLabel>
            <IonInput
              type="number"
              value={formData.materialMinStockLevel?.toString() || ''}
              onIonInput={(e) => setFormData({ ...formData, materialMinStockLevel: parseFloat(e.detail.value!) || 0 })}
              placeholder="0"
            />
          </IonItem>
        </>
      )}

      {/* Submit Button */}
      <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
        <IonButton
          expand="block"
          fill="outline"
          onClick={() => setFormData({
            name: '',
            categoryId: '',
            type: MenuItemType.MANUFACTURED,
            status: 'active',
            materialName: '',
            materialUnit: Unit.GRAM,
            materialCostPerUnit: 0,
            materialCurrentStock: 0,
            materialMinStockLevel: 0,
          })}
        >
          {t('reset') || 'Reset'}
        </IonButton>
        <IonButton
          expand="block"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? <IonLoading spinner="circles" /> : t('addProduct') || 'Add Product'}
        </IonButton>
      </div>
    </div>
  );
}